import {
	AnnotationAlertIcon,
	CheckIcon,
	ClockIcon,
	XCircleIcon
} from '@/components/ui/icons'
import { cn } from '@/helpers/utils'
import {
	FILTER_PREVIOUS_LABEL,
	OVERVIEW_CARD_CLASSNAME
} from '@/pages/luxe-submissions-mm-data-overview'
import i18n, { useTranslation } from '@/translations/i18n'
import type {
	MMDataOverviewDatePreset,
	MMDataOverviewStatsItem
} from '../types'

export interface MMOverviewStatCardProps {
	item: MMDataOverviewStatsItem
	filter: MMDataOverviewDatePreset | 'custom_range'
}

const CARD_CONFIG = {
	need_your_action: {
		icon: <ClockIcon className='size-4 text-warning-600' />,
		iconBgClassName: 'bg-warning-100',
		titleKey: i18n.t('luxe_submissions:mm_need_your_action'),
		subtitle: i18n.t('luxe_submissions:mm_ready_for_quote'),
		hasChange: false
	},
	deployed: {
		icon: <CheckIcon className='size-4 text-success-600' />,
		iconBgClassName: 'bg-success-100',
		titleKey: i18n.t('luxe_submissions:mm_generated_walkthrough'),
		subtitle: null,
		hasChange: true
	},
	avg_days_to_deploy: {
		icon: <AnnotationAlertIcon className='size-4 text-neutral-500' />,
		iconBgClassName: 'bg-neutral-100',
		titleKey: i18n.t('luxe_submissions:mm_avg_days_to_generate_quote'),
		subtitle: null,
		hasChange: true
	},
	failed_rejected: {
		icon: <XCircleIcon className='size-4 text-danger-600' />,
		iconBgClassName: 'bg-danger-100',
		titleKey: i18n.t('luxe_submissions:mm_failed_rejected'),
		subtitle: null,
		hasChange: true
	}
} as const satisfies Record<
	MMDataOverviewStatsItem['actionKeyName'],
	{
		icon: React.ReactNode
		iconBgClassName: string
		titleKey: string
		subtitle: string | null
		hasChange: boolean
	}
>

const formatChangeValue = (
	actionKeyName: MMDataOverviewStatsItem['actionKeyName'],
	value: number
): string => {
	const abs = Math.abs(value)

	if (actionKeyName === 'avg_days_to_deploy') {
		const days = i18n.t('common:days', { count: abs })
		if (value > 0) {
			return `+${days}`
		}
		if (value < 0) {
			return `-${days}`
		}
		return days
	}

	if (actionKeyName === 'failed_rejected') {
		if (value > 0) {
			return `${abs} ${i18n.t('common:more')}`
		}
		if (value < 0) {
			return `${abs} ${i18n.t('common:less')}`
		}
		return String(abs)
	}

	if (value > 0) {
		return `+${abs}%`
	}
	if (value < 0) {
		return `-${abs}%`
	}
	return `${abs}%`
}

const INVERTED_CHANGE_KEYS: readonly MMDataOverviewStatsItem['actionKeyName'][] =
	['failed_rejected', 'avg_days_to_deploy']

const getChangeColorClass = (
	actionKeyName: MMDataOverviewStatsItem['actionKeyName'],
	value: number
): string => {
	if (value === 0) {
		return 'text-neutral-500'
	}
	const isInverted = INVERTED_CHANGE_KEYS.includes(actionKeyName)
	const isGood = isInverted ? value < 0 : value > 0
	return isGood ? 'text-success-600' : 'text-danger-600'
}

function MMOverviewStatCard({ item, filter }: MMOverviewStatCardProps) {
	const { t } = useTranslation()

	const config = CARD_CONFIG[item.actionKeyName]

	return (
		<div className={OVERVIEW_CARD_CLASSNAME}>
			<div className='flex items-center gap-3'>
				<div
					className={cn(
						'flex size-8 items-center justify-center rounded-full',
						config.iconBgClassName
					)}
				>
					{config.icon}
				</div>
				<span className='font-medium text-grey-1000 text-sm'>
					{config.titleKey}
				</span>
			</div>
			<div className='flex h-full flex-col gap-1 rounded-lg bg-neutral-50 p-4'>
				<span className='font-semibold text-lg text-neutral-900'>
					{item.count}{' '}
					{item.actionKeyName === 'avg_days_to_deploy'
						? t('luxe_submissions:day', { count: item.count })
						: t('luxe_submissions:listing', { count: item.count })}
				</span>

				{config.subtitle && (
					<span className='text-neutral-700 text-sm'>
						{config.subtitle}
					</span>
				)}

				{config.hasChange &&
					item.changeValue == null &&
					item.count > 0 && (
						<span className='font-medium text-success-600'>
							{t('common:new')}
						</span>
					)}

				{config.hasChange && item.changeValue != null && (
					<span className='text-neutral-500 text-sm'>
						<span
							className={cn(
								'font-medium',
								getChangeColorClass(
									item.actionKeyName,
									item.changeValue
								)
							)}
						>
							{formatChangeValue(
								item.actionKeyName,
								item.changeValue
							)}
						</span>
						{` vs ${FILTER_PREVIOUS_LABEL[filter]}`}
					</span>
				)}
			</div>
		</div>
	)
}

export { MMOverviewStatCard }
